<template>
  <LoadingScreen loadingText="로그인 중입니다..." />
</template>

<script>
import axios from "axios";
import { useUserStore } from "../../store/user";
import { auth, storage } from "../../../firebaseConfig";
import { signInWithCustomToken, signInWithEmailAndPassword } from "firebase/auth";
import { ref as storageRef, getDownloadURL } from "firebase/storage";
import { logoutFromFirebase } from "../../utils/auth";
import { joinUser, handleLogin } from "@/utils/write/user";
import { trackLoginActivity, getDeviceInfo } from "@/utils/activityTracker";
import { TERMS_NOTICE_VERSION } from "@/utils/termsNotice";
import LoadingScreen from "@/components/LoadingScreen.vue";

export default {
  name: "KakaoLogin",
  components: {
    LoadingScreen,
  },
  data() {
    return {
      userStore: useUserStore(),
    };
  },
  created() {
    const urlParams = new URLSearchParams(window.location.search);
    // code가 있는 경우 토큰 발급 요청
    if (urlParams.has("code")) {
      const code = urlParams.get("code");
      this.setKakaoToken(code);
    }
  },
  methods: {
    // 2. 토큰 조회
    // https://developers.kakao.com/docs/latest/ko/kakaologin/rest-api#request-token
    async setKakaoToken(code) {
      // console.log("setKakaoToken");
      const { data } = await this.getKakaoToken(code);
      if (data.error) {
        // console.log(data.error);
        return;
      }
      window.Kakao.Auth.setAccessToken(data.access_token);
      await this.setUserInfo();
    },
    // 3. 사용자 정보 조회
    // https://developers.kakao.com/docs/latest/ko/kakaologin/js#req-user-info
    async setUserInfo() {
      const res = await this.getKakaoUserInfo();
      const userInfo = res.kakao_account;

      if (!userInfo?.email) {
        console.error("잘못된 유저 정보입니다.", userInfo);
        this.$alert("로그인에 실패했습니다. 메인으로 돌아갑니다.");
      } else {
        const firebasePassword = `kakao_${userInfo.email}`;

        // Firebase 로그인으로 기존 사용자 검증
        let userData;
        let profileImage = null;
        let isNewUser = true;

        try {
          // ✅ Step 1: Firebase Auth로 기존 사용자 검증
          const userCredential = await signInWithEmailAndPassword(
            auth,
            userInfo.email,
            firebasePassword
          );

          const idToken = await userCredential.user.getIdToken();
          const deviceInfo = getDeviceInfo();
          userData = await handleLogin(userInfo.email, "kakao", deviceInfo, idToken);
          isNewUser = false;

        } catch (firebaseError) {
          // Firebase 로그인 실패 = 신규 사용자
          // console.log("신규 사용자입니다");

          // ✅ Step 2: 신규 사용자 회원가입 처리
          try {
            const confirmedAt = new Date().toISOString();

            // authProviders 구조 생성
            const authProviders = [{
              provider: "kakao",
              providerUserId: res.id ? res.id.toString() : null,
            }];

            const nickname = userInfo.profile?.nickname || userInfo.name;
            const newUserData = {
              provider: "kakao",
              kakaoId: res.id ? res.id.toString() : null,
              name: nickname,
              authProviders: authProviders,
              levelPoint: 0,
            };

            // 백엔드에서 Firebase Auth + Firestore 생성 및 Custom Token 발급
            const result = await joinUser(userInfo.email, nickname, {
              password: firebasePassword,
              termsNoticeConfirmedVersion: TERMS_NOTICE_VERSION,
              termsNoticeConfirmedAt: confirmedAt,
              ...newUserData,
            });

            // Custom Token으로 자동 로그인
            if (result.customToken) {
              const userCredential = await signInWithCustomToken(auth, result.customToken);
              const idToken = await userCredential.user.getIdToken();
              const deviceInfo = getDeviceInfo();
              userData = await handleLogin(userInfo.email, "kakao", deviceInfo, idToken);
            }
          } catch (error) {
            console.error("회원가입 실패:", error);
            this.$alert("회원가입에 실패했습니다. " + (error.response?.data?.message || error.message));
            this.$router.push({ path: "/" });
            return;
          }
        }

        // ✅ 공통 로직: 로그인 성공 후 처리
        // 로그인 활동 기록
        await trackLoginActivity("kakao");

        // Firebase Storage에서 프로필 이미지 로드
        if (userData.img) {
          try {
            const imageRef = storageRef(storage, `profile/${userData.img}`);
            profileImage = await getDownloadURL(imageRef);
          } catch (error) {
            console.warn("프로필 이미지 로드 실패:", error);
          }
        }

        this.userStore.setUserData({
          ...userInfo,
          nickname: userData.nickname,
          auth: userData.auth || null,
          profileImage,
          firebaseAuthEnabled: true,
          levelPoint: userData.levelPoint || 0,
          isBlocked: userData.isBlocked || false,
        });

        // 로그인 성공 후 팝업 닫기
        if (isNewUser) {
          this.$alert("회원가입되었습니다. 링꾹에 오신 것을 환영합니다");
        }
        this.userStore.loginPopup = false;
        this.$router.push({ path: "/" });
      }

    },
    async getKakaoToken(code) {
      try {
        const data = {
          grant_type: "authorization_code",
          client_id: "b9fbcd521f83d7d672067a9d8abf5781",
          redirect_uri: process.env.VUE_APP_BASE_URL + "/kakaoLogin",
          code: code,
        };

        const queryString = Object.keys(data)
          .map((k) => encodeURIComponent(k) + "=" + encodeURIComponent(data[k]))
          .join("&");

        const result = await axios.post("https://kauth.kakao.com/oauth/token", queryString, {
          headers: {
            "Content-type": "application/x-www-form-urlencoded;charset=utf-8",
          },
        });
        return result;
      } catch (e) {
        this.$alert(
          `카카오 토큰을 받아오는데 실패했습니다. 잠시 후 다시 시도해주세요. 오류코드 : [${e.response.data?.error_code}]`
        );
        return e;
      }
    },
    async getKakaoUserInfo() {
      let data = "";
      await window.Kakao.API.request({
        url: "/v2/user/me",
      })
        .then(function (response) {
          data = response;
        })
        .catch(function () {
          // 카카오 사용자 정보 조회 실패 (무시)
        });
      return data;
    },
    // 로그아웃
    async kakaoLogout() {
      const { logOut } = useUserStore();

      // Firebase Auth 로그아웃
      await logoutFromFirebase();

      // 카카오 로그아웃
      window.Kakao.Auth.logout()
        .then(function () {
          logOut();
          // console.log(window.Kakao.Auth.getAccessToken()); // null
          // console.log(response);
        })
        .catch(function (error) {
          console.error(error);
        });
    },
  },
};
</script>

<style scoped>
/* 공통 LoadingScreen 컴포넌트 사용으로 스타일 제거 */
</style>
